import cn from 'classnames'
import {ErrorMessage, Formik, FormikHelpers, useField} from 'formik'
import {FC, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextVariant} from '@/components/common/content/Text'
import {FormErrorMessage} from '@/components/common/form/FormErrorMessage'
import {FormItem} from '@/components/common/form/FormItem'
import {ChevronIcon} from '@/components/common/icons/ChevronIcon'
import {TextInput} from '@/components/inputs/TextInput'
import {Spinner} from '@/components/Spinner'
import {useFormSummaryContent} from '@/content/formSummary'
import {useTranslation} from '@/i18n'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {REST_METHODS} from '@/utils/api/types'
import {DiscountCouponEntity} from '@/utils/constants'
import {DiscountCouponChange} from '@/utils/discount-coupon/types'
import {routeTo} from '@/utils/router'
import {fetch} from '@/utils/sentry-fetch/browser'

import {SnackbarType} from '../../snackbar/constants'
import {useSnackbarContext} from '../../snackbar/context/SnackbarContext'

type DiscountCouponProps = {
  applyDiscountCoupon?: (args: {
    discountCouponId: string
    entityType?: DiscountCouponEntity
  }) => Promise<DiscountCouponChange | null>
  onDiscountCouponChanged?: (change: DiscountCouponChange) => void
  entityType: DiscountCouponEntity
}

type DiscountCouponValues = {
  discount_coupon_id: string
}

const verifyDiscountCoupon = async ({
  discountCouponId,
  entityType,
}: {
  discountCouponId: string
  entityType: DiscountCouponEntity
}): Promise<DiscountCouponChange | null> => {
  const response = await fetch(routeTo.api.discountCoupon.verify(discountCouponId), {
    method: REST_METHODS.POST,
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      entityType,
    }),
  })

  if (!response.ok) {
    throw new Error('Failed to verify coupon')
  }

  const data = await response.json()
  return data ? {coupon: data.coupon} : null
}

export const DiscountCoupon: FC<DiscountCouponProps> = ({
  applyDiscountCoupon = verifyDiscountCoupon,
  onDiscountCouponChanged,
  entityType,
}) => {
  const {t} = useTranslation()
  const {
    applyDiscountCouponTitle,
    applyDiscountCouponLabel,
    applyDiscountCouponButton,
    appliedDiscountDescription,
  } = useFormSummaryContent()
  const {showSnackbar} = useSnackbarContext()

  const [field, , {setValue: setParentFormDiscountCouponId}] = useField('discount_coupon_id')

  const [isInputVisible, setIsInputVisible] = useState(false)

  const handleSubmit = async (
    values: DiscountCouponValues,
    {setFieldError}: FormikHelpers<DiscountCouponValues>,
  ) => {
    if (!values.discount_coupon_id) {
      return
    }
    try {
      const data = await applyDiscountCoupon({
        discountCouponId: values.discount_coupon_id,
        entityType,
      })
      if (data?.coupon) {
        onDiscountCouponChanged?.(data)
        setParentFormDiscountCouponId(data.coupon.id, false)
        showSnackbar(t(appliedDiscountDescription), true, SnackbarType.success)
      } else {
        setFieldError('discount_coupon_id', t('error-invalid-coupon'))
      }
    } catch (e) {
      setFieldError('discount_coupon_id', t('error-couldnt-apply'))
    }
  }

  return (
    !field.value && (
      <>
        <button onClick={() => setIsInputVisible(!isInputVisible)} type="button" className="button">
          <Text variant={TextVariant.h5} element="span" align="left">
            {t(applyDiscountCouponTitle)}
          </Text>
          <ChevronIcon isActive={isInputVisible} />
        </button>
        <Formik initialValues={{discount_coupon_id: ''}} onSubmit={handleSubmit}>
          {({submitForm, isSubmitting}) => (
            <div className={cn('wrapper', isInputVisible && 'expanded')}>
              <FormItem label={t(applyDiscountCouponLabel)}>
                <ErrorMessage
                  name="discount_coupon_id"
                  render={(msg) => <FormErrorMessage message={msg} />}
                />
                <TextInput
                  name="discount_coupon_id"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      submitForm()
                    }
                  }}
                />
              </FormItem>
              <div className="button-wrapper">
                <Button
                  variant={ButtonVariant.primary}
                  type="button"
                  onClick={submitForm}
                  fullWidth
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Spinner color={theme.palette.text.white} size="small" />
                  ) : (
                    t(applyDiscountCouponButton)
                  )}
                </Button>
              </div>
            </div>
          )}
        </Formik>

        <style jsx>{`
          .wrapper {
            overflow: hidden;
            visibility: hidden;
            opacity: 0;
            max-height: 0;
            transition: all ${EXPAND_TRANSITION}ms ease-in-out;
          }

          .expanded {
            visibility: visible;
            opacity: 1;
            max-height: 165px;
            margin-top: ${theme.spacing[4]};
          }

          .button {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
          }

          .button-wrapper {
            margin-top: ${theme.spacing[3]};
          }
        `}</style>
      </>
    )
  )
}
